import { useEffect, useRef, useState } from 'react'
import type { Preview, TriggerSpec } from '@/frontend/features/Fde/triggers/types'
import { postJson } from '@/frontend/features/Fde/triggers/types'

/** A preview belongs to exactly one source and project. Discard late responses
 * and run computations only after an explicit simulation request. */
export default function useTriggerPreview(spec: TriggerSpec | undefined, url: string, triggerId?: string) {
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const run = useRef(0)
  const controller = useRef<AbortController | null>(null)
  const key = spec ? JSON.stringify(spec) : ''
  async function execute(current: TriggerSpec) {
    controller.current?.abort()
    const request = new AbortController()
    controller.current = request
    const mine = ++run.current
    setBusy(true); setError(null)
    try {
      const out = await postJson<Preview>(url, { spec: current, trigger_id: triggerId }, request.signal)
      if (mine === run.current) setPreview(out)
    } catch (err) {
      if (mine === run.current && !request.signal.aborted) setError((err as Error).message)
    } finally {
      if (mine === run.current) setBusy(false)
    }
  }
  useEffect(() => {
    ++run.current; controller.current?.abort()
    setPreview(null); setError(null); setBusy(false)
    const automatic = spec && !['python', 'computation'].includes(spec.source.kind)
    const timer = automatic ? window.setTimeout(() => execute(spec), 900) : undefined
    return () => { window.clearTimeout(timer); ++run.current; controller.current?.abort() }
    // The serialized spec is the input contract, independent of object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, url, triggerId])
  return { preview, error, busy, refresh: () => spec && execute(spec) }
}
